import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCustomerFacilities,
  upsertCustomerFacility,
  deleteCustomerFacility,
} from "@/lib/routing.functions";
import { listTenants } from "@/lib/admin-config.functions";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/customer-facilities")({
  component: CustomerFacilitiesPage,
});

/** Sprint 9 — Customer Facility Eligibility admin.
 *  Manages which facilities each customer is contracted to operate at.
 *  This is the data primitive the router consumes BEFORE ranking;
 *  no routing logic lives here. */
function CustomerFacilitiesPage() {
  const { roles } = useAuth();
  const { isSuperAdmin: su } = useScope();
  const tenantIds = useMemo(() => {
    if (su) return null;
    return tenantIdsWithRole(roles, "tenant_admin");
  }, [roles, su]);

  const fetchTenants = useServerFn(listTenants);
  const { data: tenants = [] } = useQuery({
    queryKey: ["admin", "cfe", "tenants"],
    queryFn: () => fetchTenants(),
  });
  const allowed = useMemo(
    () => (tenantIds === null ? tenants : tenants.filter((t: any) => tenantIds.includes(t.id))),
    [tenants, tenantIds],
  );
  const [tenantId, setTenantId] = useState<string>("");
  if (!tenantId && allowed.length) setTenantId(allowed[0].id);

  const { data: customers = [] } = useQuery({
    queryKey: ["admin", "cfe", "customers", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers").select("id, name")
        .eq("tenant_id", tenantId).order("name");
      if (error) throw error;
      return data ?? [];
    },
  });
  const [customerId, setCustomerId] = useState<string>("");
  if (!customerId && customers.length) setCustomerId((customers[0] as any).id);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Customer Facility Eligibility</h1>
          <p className="text-sm text-muted-foreground">
            Which facilities each customer is contracted to operate at. The router uses this set
            <strong> before</strong> ranking — a customer with no memberships is not eligible at any facility (default-deny).
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={tenantId} onValueChange={(v) => { setTenantId(v); setCustomerId(""); }}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="Tenant" /></SelectTrigger>
            <SelectContent>
              {allowed.map((t: any) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="w-[240px]"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent>
              {(customers as any[]).map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </header>
      {tenantId && customerId ? (
        <Memberships tenantId={tenantId} customerId={customerId} />
      ) : (
        <p className="text-sm text-muted-foreground">Pick a tenant and customer to manage memberships.</p>
      )}
    </div>
  );
}

function Memberships({ tenantId, customerId }: { tenantId: string; customerId: string }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listCustomerFacilities);
  const upsertFn = useServerFn(upsertCustomerFacility);
  const deleteFn = useServerFn(deleteCustomerFacility);
  const queryKey = ["admin", "cfe", "memberships", tenantId, customerId];
  const { data, isLoading } = useQuery({
    queryKey, queryFn: () => listFn({ data: { tenantId, customerId } }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const upsert = useMutation({
    mutationFn: (vars: { facilityId: string; active: boolean }) =>
      upsertFn({ data: { tenantId, customerId, ...vars } }),
    onSuccess: () => { invalidate(); toast.success("Membership saved"); },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });
  const del = useMutation({
    mutationFn: (membershipId: string) => deleteFn({ data: { tenantId, membershipId } }),
    onSuccess: () => { invalidate(); toast.success("Membership removed"); },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const facilities = data?.facilities ?? [];
  const memByFac = new Map((data?.memberships ?? []).map((m) => [m.facility_id, m]));

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left">
          <tr>
            <th className="px-3 py-2">Facility</th>
            <th className="px-3 py-2 w-32">Eligible</th>
            <th className="px-3 py-2 w-32">Active</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 w-32"></th>
          </tr>
        </thead>
        <tbody>
          {facilities.map((f) => {
            const m = memByFac.get(f.id);
            const enabled = !!m;
            return (
              <tr key={f.id} className="border-t">
                <td className="px-3 py-2 font-medium">{f.name}</td>
                <td className="px-3 py-2">
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => {
                      if (v) upsert.mutate({ facilityId: f.id, active: true });
                      else if (m) del.mutate(m.id);
                    }}
                  />
                </td>
                <td className="px-3 py-2">
                  {enabled ? (
                    <Switch
                      checked={!!m?.active}
                      onCheckedChange={(v) => upsert.mutate({ facilityId: f.id, active: v })}
                    />
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {!enabled ? <Badge variant="outline">not eligible</Badge>
                    : !m?.active ? <Badge variant="destructive">paused</Badge>
                    : <Badge variant="secondary">eligible</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  {enabled && m ? (
                    <Button size="sm" variant="ghost" onClick={() => del.mutate(m.id)}>Remove</Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}